import { type BeamstarSession, StatusVariant } from '@consigli/types';
import { type ColumnDef } from '@tanstack/react-table';

import { Badge } from '@/components';

import { counted, formatCount, formatDateTime, formatTokens } from '../format';
import { WorkflowChips } from '../workflow-chips';
import { sessionProblems } from './session-problems';

export const sessionColumns: ColumnDef<BeamstarSession>[] = [
  {
    header: 'Started',
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatDateTime(row.original.startedAt)}
      </span>
    ),
  },
  {
    header: 'Session',
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="font-medium">{row.original.buildingId ?? 'No building'}</span>
        <span className="text-xs text-muted-foreground">
          {row.original.projectId === null ? 'No project' : `Project ${row.original.projectId}`}
        </span>
      </div>
    ),
  },
  {
    header: 'Workflows',
    cell: ({ row }) => <WorkflowChips workflows={row.original.workflows} />,
  },
  { header: 'Turns', cell: ({ row }) => formatCount(row.original.turns) },
  {
    header: 'Problems',
    cell: ({ row }) => {
      const problems = sessionProblems(row.original);
      if (problems.length === 0) {
        return <span className="text-muted-foreground">-</span>;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {problems.map((problem) => (
            <Badge key={problem.text} text={problem.text} variant={problem.variant} />
          ))}
        </div>
      );
    },
  },
  {
    header: 'Tokens',
    cell: ({ row }) => (
      <div className="flex flex-col whitespace-nowrap">
        <span>
          {formatTokens(row.original.inputTokens)} in · {formatTokens(row.original.outputTokens)}{' '}
          out
        </span>
        <span className="text-xs text-muted-foreground">
          {formatTokens(row.original.cachedInputTokens)} cached
        </span>
      </div>
    ),
  },
  {
    header: 'Flags',
    cell: ({ row }) =>
      row.original.flags > 0 && (
        <Badge
          text={counted(row.original.flags, 'flag', 'flags')}
          variant={StatusVariant.WARNING}
        />
      ),
  },
];
